import { Check } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { RenderCancelledError, type PdfThumbnails } from '@/tools/pdf-engine';
import { cn } from '@/lib/utils';

const THUMB_CSS_WIDTH = 104;

/**
 * One page's preview, rendered by pdf.js only while it is on screen and cancelled when it scrolls
 * away (#378: rendering is the costly part). The canvas is released on unmount.
 */
export function PageThumb({
  thumbs,
  page,
  rotate,
  selected,
  dragging,
  images,
  imagesLabel,
}: {
  readonly thumbs: Promise<PdfThumbnails>;
  readonly page: number;
  readonly rotate: number;
  readonly selected: boolean;
  readonly dragging: boolean;
  /** How many images were placed on this page. */
  readonly images: number;
  readonly imagesLabel: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let renderer: PdfThumbnails | null = null;
    let active = true;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        void thumbs
          .then((t) => {
            renderer = t;
            if (!active) return;
            if (visible) {
              t.render(page, canvas, THUMB_CSS_WIDTH, rotate).catch((error: unknown) => {
                if (!(error instanceof RenderCancelledError)) canvas.dataset.failed = 'true';
              });
            } else {
              t.cancel(page);
            }
          })
          .catch(() => undefined);
      },
      { rootMargin: '200px' },
    );
    observer.observe(canvas);
    return () => {
      active = false;
      observer.disconnect();
      renderer?.cancel(page);
      canvas.width = 0;
      canvas.height = 0;
    };
  }, [thumbs, page, rotate]);

  return (
    <span
      className={cn(
        'relative flex aspect-[1/1.4] w-full items-center justify-center overflow-hidden rounded-md border-2 bg-white shadow-sm',
        selected ? 'border-ring' : 'border-border',
        dragging && 'opacity-40',
      )}
    >
      <canvas ref={canvasRef} aria-hidden="true" className="max-h-full max-w-full" />
      {images > 0 && (
        <span className="absolute bottom-1.5 left-1.5 rounded bg-ring px-1.5 py-0.5 text-[11px] font-semibold text-white">
          {imagesLabel}
        </span>
      )}
      {selected && (
        <span className="absolute top-1.5 right-1.5 flex size-[22px] items-center justify-center rounded-md bg-ring text-white">
          <Check aria-hidden="true" className="size-3.5" />
        </span>
      )}
    </span>
  );
}
